import React, { useEffect, useState, useCallback } from 'react';
import { apiCall } from '../lib/api';

interface AuditEntry {
  id: number;
  actor_username: string | null;
  actor_role: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  payload: any;
  created_at: string;
}

export const AuditLogPage: React.FC = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '300' });
      if (actionFilter) params.append('action', actionFilter);
      const resp = await apiCall(`/api/audit?${params.toString()}`);
      if (!resp.ok) throw new Error('Failed to load audit log');
      const data = await resp.json();
      setEntries(data.entries || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          All mutating actions performed through the Control Center.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Filter by action (e.g. user.create)"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1D407B]/30 focus:border-[#1D407B]"
        />
        <button
          onClick={load}
          className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left">
              <th className="px-5 py-3 font-semibold text-gray-600">When</th>
              <th className="px-5 py-3 font-semibold text-gray-600">Actor</th>
              <th className="px-5 py-3 font-semibold text-gray-600">Action</th>
              <th className="px-5 py-3 font-semibold text-gray-600">Target</th>
              <th className="px-5 py-3 font-semibold text-gray-600">Payload</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">
                  No audit entries match the current filter.
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 last:border-0 align-top">
                  <td className="px-5 py-3 text-xs text-gray-500 font-mono whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-gray-800">{e.actor_username || '—'}</div>
                    <div className="text-xs text-gray-400">{e.actor_role || ''}</div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-gray-700">{e.action}</td>
                  <td className="px-5 py-3 text-xs text-gray-600">
                    {e.target_type ? `${e.target_type}/${e.target_id ?? '—'}` : '—'}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500">
                    {e.payload ? (
                      <pre className="whitespace-pre-wrap break-all max-w-md">
                        {typeof e.payload === 'string' ? e.payload : JSON.stringify(e.payload, null, 0)}
                      </pre>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
